/**
 * Operation Mode — run orchestration (start / tick / evolution purchase).
 * Composes the pure spread + resistance + DNA systems into whole-run transitions.
 * Still headless & deterministic (no DOM, no Math.random).
 */

import type { OperationState, OperationDifficulty } from '../state.js';
import type { OperationRace } from '../data/evolution.js';
import { aggregateEvoEffects, getEvoNode, prereqMet } from '../data/evolution.js';
import { stepInfection, allDominated } from './spread.js';
import {
  stepResistance, resistanceComplete, dnaPerTick,
  bubbleDnaAmount, mostInfiltratedRegion,
} from './resistance.js';
import { startableRegionIds } from '../data/regions.js';

/** Infiltration seeded into the chosen start region. */
export const OP_START_INFECTION = 8;
/** DNA head start for a fresh operation. */
export const OP_START_DNA = 15;
/** Ticks between DNA bubble spawns. */
export const BUBBLE_INTERVAL_TICKS = 10;

/** A blank (inactive) operation state — used to return to the start picker. */
export function blankOperation(): OperationState {
  return {
    active: false, race: null, difficulty: 'normal', infection: {},
    resistance: 0, dna: 0, boughtNodes: [], elapsedTicks: 0,
    outcome: null, startRegion: null, bubble: null,
  };
}

/**
 * Build a fresh, active operation run. Returns null if the start region is not
 * a valid startable region (China/USA are locked as final targets).
 */
export function createOperationRun(
  race: OperationRace,
  difficulty: OperationDifficulty,
  startRegion: string,
): OperationState | null {
  if (!startableRegionIds().includes(startRegion)) return null;
  return {
    active: true, race, difficulty,
    infection: { [startRegion]: OP_START_INFECTION },
    resistance: 0, dna: OP_START_DNA, boughtNodes: [],
    elapsedTicks: 0, outcome: null, startRegion, bubble: null,
  };
}

/** Whether any region has been infiltrated at all. */
function hasSpread(infection: Record<string, number>): boolean {
  return Object.values(infection).some((v) => v > 0);
}

/** Spawn a DNA bubble on the schedule if the map has spread and none is present. */
function maybeSpawnBubble(op: OperationState, infection: Record<string, number>, tick: number): OperationState['bubble'] {
  if (op.bubble) return op.bubble;
  if (tick % BUBBLE_INTERVAL_TICKS !== 0) return null;
  if (!hasSpread(infection)) return null;
  return {
    regionId: mostInfiltratedRegion(infection),
    amount: bubbleDnaAmount(infection),
    spawnedTick: tick,
  };
}

/**
 * Advance the operation one tick. Returns a NEW state, or null when frozen
 * (inactive or already resolved) so the reducer can no-op.
 */
export function advanceOperation(op: OperationState): OperationState | null {
  if (!op.active || op.outcome || !op.race) return null;

  const effects = aggregateEvoEffects(op.boughtNodes, op.race);
  const infection = stepInfection(op.infection, effects);
  const resistance = stepResistance(op.resistance, infection, effects, op.difficulty);
  const dna = op.dna + dnaPerTick(infection, effects);
  const elapsedTicks = op.elapsedTicks + 1;
  const bubble = maybeSpawnBubble(op, infection, elapsedTicks);

  let outcome: 'win' | 'lose' | null = null;
  if (allDominated(infection)) outcome = 'win';
  else if (resistanceComplete(resistance)) outcome = 'lose';

  return { ...op, infection, resistance, dna, elapsedTicks, bubble, outcome };
}

/** Whether a node can currently be purchased (owned? prereqs? enough DNA?). */
export function canBuyEvolution(op: OperationState, nodeId: string): boolean {
  const node = getEvoNode(nodeId);
  if (!node) return false;
  if (op.boughtNodes.includes(nodeId)) return false;
  if (!prereqMet(nodeId, op.boughtNodes)) return false;
  return op.dna >= node.cost;
}
